import axios from 'axios';

export const GPA_SCALE_MAX = 4;

function authHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Clamp GPA filter/slider bounds to the 4.0 scale. */
export function normalizeGpaRange(range) {
  const min = Math.max(0, Number(range?.min ?? 0));
  let max = Math.min(GPA_SCALE_MAX, Number(range?.max ?? GPA_SCALE_MAX));
  if (!Number.isFinite(max)) max = GPA_SCALE_MAX;
  return { min, max: Math.max(min, max) };
}

/** @param {Record<string, unknown>} filters */
export function buildSearchParams(filters, lang = 'ru') {
  const p = new URLSearchParams();
  p.set('lang', lang);
  p.set('page', String(filters.page ?? 1));
  p.set('per_page', String(filters.perPage ?? 12));

  const q = (filters.search ?? '').trim();
  if (q) p.set('search', q);

  const arrayKeys = [
    ['country_id', filters.countryIds],
    ['state_id', filters.stateIds],
    ['study_degree_id', filters.studyDegreeIds],
    ['scholarship_id', filters.scholarshipIds],
    ['category_id', filters.categoryIds],
    ['subcategory_id', filters.subcategoryIds],
    ['rank_id', filters.rankIds],
  ];

  for (const [key, ids] of arrayKeys) {
    (ids ?? []).forEach((id) => p.append(`${key}[]`, String(id)));
  }

  if (filters.gpa) {
    p.set('gpa_min_from', String(filters.gpa.from));
    p.set('gpa_min_to', String(filters.gpa.to));
  }
  if (filters.ielts) {
    p.set('ielts_min_from', String(filters.ielts.from));
    p.set('ielts_min_to', String(filters.ielts.to));
  }
  if (filters.duolingo !== null && filters.duolingo !== undefined) {
    p.set('duolingo', filters.duolingo ? 'true' : 'false');
  }

  return p;
}

export function applyFacetCounts(catalogItems, facetMap) {
  const dist = facetMap ?? {};
  return (catalogItems || []).map((item) => ({
    ...item,
    count: dist[String(item.id)] ?? 0,
    disabled: (dist[String(item.id)] ?? 0) === 0,
  }));
}

/**
 * Merge Meilisearch facets into catalog options for the filter UI.
 * @param {object} catalog - raw /filters response
 * @param {object} response - /search response
 * @param {object} selected - current filter ids
 */
export function mergeFacetsIntoUi(catalog, response, selected) {
  const f = response?.facets ?? {};

  let states = catalog.states ?? [];
  if (selected.countryIds?.length) {
    states = states.filter((s) => selected.countryIds.includes(s.country_id));
  }
  states = applyFacetCounts(states, f.state_id);

  let subcategories = catalog.subcategories ?? [];
  if (selected.categoryIds?.length) {
    subcategories = subcategories.filter((sc) =>
      selected.categoryIds.includes(sc.category_id)
    );
  }
  subcategories = applyFacetCounts(subcategories, f.subcategory_id);

  const duolingoFacet = f.duolingo ?? {};
  const duolingo = {
    true: duolingoFacet.true ?? catalog.duolingo?.true ?? 0,
    false: duolingoFacet.false ?? catalog.duolingo?.false ?? 0,
  };

  return {
    countries: applyFacetCounts(catalog.countries, f.country_id),
    states,
    categories: applyFacetCounts(catalog.categories, f.category_id),
    subcategories,
    study_degrees: applyFacetCounts(catalog.study_degrees, f.study_degree_id),
    scholarships: applyFacetCounts(catalog.scholarships, f.scholarship_id),
    ranks: applyFacetCounts(catalog.ranks, f.rank_id),
    duolingo,
  // Full catalog bounds for sliders — do not narrow from filtered search results.
    gpaRange: normalizeGpaRange(catalog.ranges?.gpa ?? { min: 0, max: GPA_SCALE_MAX }),
    ieltsRange: catalog.ranges?.ielts ?? { min: 5, max: 9 },
    programs: response?.data ?? [],
    meta: response?.meta ?? { total: 0, current_page: 1, last_page: 1, per_page: 12 },
  };
}

export function defaultFilterState() {
  return {
    search: '',
    countryIds: [],
    stateIds: [],
    studyDegreeIds: [],
    scholarshipIds: [],
    categoryIds: [],
    subcategoryIds: [],
    rankIds: [],
    gpa: null,
    ielts: null,
    duolingo: null,
    page: 1,
    perPage: 12,
  };
}

const QUERY_ARRAY_KEYS = [
  ['country', 'countryIds'],
  ['state', 'stateIds'],
  ['degree', 'studyDegreeIds'],
  ['scholarship', 'scholarshipIds'],
  ['cat', 'categoryIds'],
  ['subcat', 'subcategoryIds'],
  ['rank', 'rankIds'],
];

/** Serialize filter state into route-query params; default values are omitted. */
export function filtersToQuery(filters) {
  const q = {};
  const search = (filters.search ?? '').trim();
  if (search) q.search = search;
  for (const [param, key] of QUERY_ARRAY_KEYS) {
    if (filters[key]?.length) q[param] = filters[key].join(',');
  }
  if (filters.gpa) {
    q.gpa_from = String(filters.gpa.from);
    q.gpa_to = String(filters.gpa.to);
  }
  if (filters.ielts) {
    q.ielts_from = String(filters.ielts.from);
    q.ielts_to = String(filters.ielts.to);
  }
  if (filters.duolingo !== null && filters.duolingo !== undefined) {
    q.duolingo = filters.duolingo ? '1' : '0';
  }
  if ((filters.page ?? 1) > 1) q.page = String(filters.page);
  return q;
}

function parseIdList(value) {
  const raw = Array.isArray(value) ? value.join(',') : value;
  if (typeof raw !== 'string' || !raw) return [];
  return raw
    .split(',')
    .filter((v) => v !== '')
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n));
}

function parseQueryRange(from, to) {
  const f = parseFloat(from);
  const t = parseFloat(to);
  if (!Number.isFinite(f) || !Number.isFinite(t)) return null;
  return { from: f, to: t };
}

/** Restore filter state from route-query params (inverse of filtersToQuery). */
export function filtersFromQuery(query) {
  const filters = defaultFilterState();
  if (!query) return filters;
  if (typeof query.search === 'string') filters.search = query.search;
  for (const [param, key] of QUERY_ARRAY_KEYS) {
    filters[key] = parseIdList(query[param]);
  }
  filters.gpa = parseQueryRange(query.gpa_from, query.gpa_to);
  filters.ielts = parseQueryRange(query.ielts_from, query.ielts_to);
  if (query.duolingo === '1' || query.duolingo === 'true') filters.duolingo = true;
  else if (query.duolingo === '0' || query.duolingo === 'false') filters.duolingo = false;
  const page = parseInt(query.page, 10);
  if (Number.isFinite(page) && page > 1) filters.page = page;
  return filters;
}

/** Clamp a restored {from,to} range to catalog bounds; null when invalid or spanning the full range. */
export function sanitizeRestoredRange(range, bounds) {
  if (!range || !bounds) return null;
  const from = Math.max(bounds.min, Math.min(bounds.max, range.from));
  const to = Math.max(bounds.min, Math.min(bounds.max, range.to));
  if (from > to) return null;
  return from > bounds.min || to < bounds.max ? { from, to } : null;
}

export function isGpaActive(gpa, range) {
  if (!gpa || !range) return false;
  return gpa.from > range.min || gpa.to < range.max;
}

export function isIeltsActive(ielts, range) {
  if (!ielts || !range) return false;
  return ielts.from > range.min || ielts.to < range.max;
}

export async function loadFilterCatalog(lang) {
  const { data } = await axios.get('/platform/search/university-programs/filters', {
    headers: authHeaders(),
    params: { lang },
  });
  return data;
}

export async function searchUniversityPrograms(filters, lang) {
  const qs = buildSearchParams(filters, lang);
  const { data } = await axios.get(`/platform/search/university-programs?${qs}`, {
    headers: authHeaders(),
  });
  return data;
}

/** Scholarships that open external sites instead of filtering */
export const EXTERNAL_SCHOLARSHIP_IDS = [4, 5, 7];

export function openExternalScholarship(id) {
  if (id === 4) {
    window.open(
      'https://www.mastersportal.com/search/master/affordable/germany?de=fulltime&mh=face2face',
      '_blank'
    );
    return true;
  }
  if (id === 5) {
    window.open('https://stipendiumhungaricum.hu/study-finder/', '_blank');
    return true;
  }
  if (id === 7) {
    window.open(
      'https://shorelight.com/universities/graduate?q=&sort=signature-asc&compareList=&filter=eyJzZW1lc3RlcnMiOltdLCJzZXJ2aWNlcyI6e30sImlzU2Nob2xhcnNoaXBzIjpmYWxzZSwicHJvZ3JhbUxldmVsIjoiIiwidHVpdGlvbnMiOltdLCJzdGFydFNlbWVzdGVyIjoiIiwiYWRtaXNzaW9uc1N1cHBvcnQiOltdLCJyYW5raW5nIjoiIiwicmVnaW9ucyI6W10sImxvY2F0aW9uVHlwZXMiOltdLCJ0b3RhbFN0dWRlbnRFbnJvbGxtZW50cyI6W10sImludGVybmF0aW9uYWxTdHVkZW50UG9wdWxhdGlvbnMiOltdLCJncGEiOltdLCJpZWx0cyI6W10sInRvZWZsIjpbXX0%3D',
      '_blank'
    );
    return true;
  }
  return false;
}
